import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import InputField from 'components/Form/InputField';
import { TempControlFacilityType } from '__generated-api__';

import RadioHighGroup, { radioHighOptionsListClassName } from 'tempcontrol/components/radio-high-group';
import { facilityTypeOptions, sizingMethodForFacility } from 'tempcontrol/enums';
import { WizardFormValues } from 'tempcontrol/validations/schemas';

interface Step3ConfigurationProps {
  isReadOnly?: boolean;
}

/**
 * Step 2 of 3 - project configuration inputs using the tempcontrol s3 cutup
 * form patterns (`c-form-element--radio--high` cards, etc.).
 *
 * Add-on options (piping package, factory assembled, etc.) used to live here
 * behind a "Show advanced options" toggle. They only build the part-number
 * suffix - not the sizing - so they now live in an "Available Options"
 * accordion on the results page (see `ResultsAccordions`), next to the
 * recommended product.
 */
export const Step3Configuration: React.VFC<Step3ConfigurationProps> = ({ isReadOnly }) => {
  const { setValue } = useFormContext<WizardFormValues>();
  const facility = useWatch<WizardFormValues>({ name: 'facility' }) as TempControlFacilityType | undefined;

  // Allowable PSI defaults to 10 up-front (see projectToFormValues), so this
  // effect only maps the facility to its internal flow-derivation method.
  React.useEffect(() => {
    if (!facility) return;
    setValue('method', sizingMethodForFacility(facility), { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facility]);

  return (
    <>
      <h5 className="u-mb-spacer-base-small">Building Information</h5>
      <p className="u-mb-spacer-base">
        The building type determines which sizing parameters to use during calculation.
      </p>
      <fieldset disabled={isReadOnly} className="u-mb-0">
        <div className="o-row o-row--small-gutters">
          <div className="o-col-12@sm u-mb-spacer-base-small">
            <RadioHighGroup
              name="facility"
              options={facilityTypeOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
                description: opt.description,
              }))}
              optionsClassName={radioHighOptionsListClassName}
              disabled={isReadOnly}
            />
          </div>

          <div className="o-col-12@sm u-mb-spacer-base-small">
            <InputField
              name="allowable_psi"
              type="number"
              elStyle="fill"
              label="Allowable PSI Drop"
              helpText="Select the pressure the valve may consume during operation. Symmons recommends between 5-15 PSI."
              inputProps={{ min: 1, max: 50, step: 1 }}
            />
          </div>
        </div>
      </fieldset>
    </>
  );
};

export default Step3Configuration;
